import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  TUpdateLabelCustomerValidation,
  updateLabelCustomerValidationSchema,
  useUpdateLabelCustomerMutation,
} from '../../../api/update-label-customer.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LABEL_CUSTOMER_QUERY_KEY } from '../../../api/get-list-label-customer.api';
import { useCustomerModuleModalRootActionsStore } from '@/features/customers/_shared/components/customer-modal-root/customer-modal-root.store';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
// import { GENERAL_LABEL_COLOR } from '@/features/settings/label/_shared/constants/general-label-color';
import { GENERAL_LABEL_COLOR } from '@/features/cases/_shared/constants/cases.contants';
import { cn } from '@/utils/cn';
import { Input } from '@/components/_ui/input';
import { Spinner } from '@/components/_ui/spinner';

export type TUpdateLabelCustomerModalType = 'UpdateLabelCustomerModal';
export interface IUpdateLabelCustomerModalProps {
  labelCustomerId: string;
  name?: string;
  color?: string;
}
const UpdateLabelCustomerModal = memo(({ labelCustomerId, name, color }: IUpdateLabelCustomerModalProps) => {
  const customerModuleModalRootActions = useCustomerModuleModalRootActionsStore();

  const isOpenUpdateLabelCustomerModal = useMemo(() => {
    return customerModuleModalRootActions.checkIsOpenModal('UpdateLabelCustomerModal');
  }, [customerModuleModalRootActions]);

  const showToast = useCustomToast();
  const form = useForm<TUpdateLabelCustomerValidation>({
    resolver: zodResolver(updateLabelCustomerValidationSchema),
    mode: 'onChange',
    defaultValues: {
      labelCustomerId: '',
      name: '',
      color: '',
    },
  });

  useEffect(() => {
    if (labelCustomerId || name || color) {
      form.reset({
        labelCustomerId: labelCustomerId || '',
        name: name || '',
        color: color || '',
      });
    }
  }, [labelCustomerId, name, color, form]);

  const mutationUpdateLabelCustomer = useUpdateLabelCustomerMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error update label customer, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_CUSTOMER_QUERY_KEY] });
        customerModuleModalRootActions.closeModal('UpdateLabelCustomerModal');
        form.reset();
        showToast({
          message: 'Label customer update successfully',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitUpdateLabelCustomer = useCallback(
    (data: TUpdateLabelCustomerValidation) => {
      mutationUpdateLabelCustomer.mutate({
        labelCustomerId: data?.labelCustomerId || '',
        name: data?.name,
        color: data?.color,
      });
    },
    [mutationUpdateLabelCustomer]
  );

  const handleClose = useCallback(() => {
    customerModuleModalRootActions.closeModal('UpdateLabelCustomerModal');
    form.reset();
  }, [customerModuleModalRootActions, form]);
  return (
    <Modal isOpen={isOpenUpdateLabelCustomerModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Edit Label Customer</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitUpdateLabelCustomer)(e);
          }}
          id="edit-label-customer-form"
          className="grid gap-[16px]"
        >
          <div className="grid gap-[4px]">
            <label htmlFor="name-label-customer" className="text-[#4B4B4B] text-[14px] font-normal">
              Name*
            </label>
            <Input id="name-label-customer" placeholder="Enter Label Customer Name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="grid gap-[4px]">
            <label htmlFor="color-label-customer" className="text-[#4B4B4B] text-[14px] font-normal">
              Color*
            </label>
            <div id="color-label-customer" className="grid grid-cols-3 gap-[22px]">
              {Object.entries(GENERAL_LABEL_COLOR).map(([key, color], index) => {
                const isSelected = form.watch('color') === color;
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      handleDomBubbleEvent(e);
                      form.setValue('color', color);
                    }}
                    key={index + 1}
                    className={cn(
                      'w-full gap-[6px] flex justify-start py-[8px] px-[6px] rounded-[8px] bg-[#F1F5F9] cursor-pointer border-transparent border-2 hover:border-[#0C8DBF] hover:bg-[#0C8DBF]/20 transition-all duration-300',
                      isSelected && 'border-[#0C8DBF] bg-[#0C8DBF]/20'
                    )}
                  >
                    <div className="w-[16px] h-[16px] rounded-full " style={{ backgroundColor: color }}></div>
                    <span className="text-[#25151E] text-[12px] font-normal">{key}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          form="edit-label-customer-form"
          type="submit"
          disabled={mutationUpdateLabelCustomer.isPending}
        >
          {mutationUpdateLabelCustomer.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}{' '}
          Save
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
UpdateLabelCustomerModal.displayName = 'UpdateLabelCustomerModal';

export default UpdateLabelCustomerModal;
